import React, { useEffect, useRef, useState } from "react";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LockOutlined, EmailOutlined, Login } from "@mui/icons-material";

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.toLowerCase());
};

const LoginPage = () => {
  const navigate = useNavigate();
  const errRef = useRef();

  const [email, setEmail] = useState(
    () => localStorage.getItem("sal_remembered_email") || "",
  );
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(
    () => !!localStorage.getItem("sal_remembered_email"),
  );
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setErrMsg("");
    if (auth.currentUser) navigate("/home");
  }, [navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrMsg("");

    if (!validateEmail(email))
      return setErrMsg("Please enter a valid email address");
    if (!password) return setErrMsg("Please enter your password");

    setLoading(true);
    try {
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence,
      );
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (rememberMe) {
        localStorage.setItem("sal_remembered_email", email);
      } else {
        localStorage.removeItem("sal_remembered_email");
      }
      const name = cred.user.email?.split("@")[0] || "Admin";
      toast(`Welcome back, ${name}!`, { type: "success" });
      navigate("/home");
    } catch (error) {
      if (error.code === "auth/user-not-found") {
        setErrMsg("No account found with this email");
      } else if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        setErrMsg("Email or password is incorrect");
      } else {
        setErrMsg("Login failed. Please try again.");
      }
      errRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div className="hidden md:flex md:w-1/2 lg:w-3/5 bg-[#000C66] flex-col items-center justify-center p-16 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white opacity-[0.03]" />
        <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-[#FFC000] opacity-[0.06]" />
        <div className="absolute top-1/3 right-10 w-40 h-40 rounded-full bg-white opacity-[0.03]" />

        <div className="relative z-10 text-center max-w-lg">
          <div className=" rounded-2xl flex items-center justify-center mx-auto mb-8 ">
            {/* <span className="text-[#000C66] font-bold text-3xl">✝</span> */}
            <img
              src="https://rentnowltd.s3.us-east-2.amazonaws.com/salvation/salv_logo.png"
              alt="salv logo"
              className="h-24 w-24 rounded-full object-cover border border-gray-200 flex-shrink-0"
            />
          </div>

          <h1 className="text-5xl font-aref font-bold text-white tracking-wider mb-3">
            SALVATION
          </h1>
          <p className="text-[#FFC000] font-abel text-xl tracking-widest uppercase mb-10">
            Administration Portal
          </p>

          <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-10" />

          <p className="text-blue-200 font-abel text-base leading-relaxed italic">
            "For I know the plans I have for you, declares the Lord, plans to
            prosper you and not to harm you, plans to give you hope and a
            future."
          </p>
          <p className="text-[#FFC000] font-abel text-sm mt-3">
            — Jeremiah 29:11
          </p>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="w-full md:w-1/2 lg:w-2/5 flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex md:hidden flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-xl bg-[#000C66] flex items-center justify-center mb-3">
              <span className="text-[#FFC000] font-bold text-2xl">✝</span>
            </div>
            <h1 className="text-2xl font-aref font-bold text-[#000C66]">
              SALVATION
            </h1>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#000C66] font-aref">
              Welcome Back
            </h2>
            <p className="text-gray-400 text-sm font-abel mt-1">
              Sign in to your admin account
            </p>
          </div>

          {errMsg && (
            <div
              ref={errRef}
              tabIndex={-1}
              className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-abel flex items-start gap-2"
              aria-live="assertive"
            >
              <span className="mt-0.5">⚠</span> {errMsg}
            </div>
          )}

          <div className="space-y-4" onKeyDown={handleKeyDown}>
            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">
                Email Address
              </label>
              <div className="relative">
                <EmailOutlined
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  style={{ fontSize: 18 }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrMsg("");
                  }}
                  placeholder="admin@example.com"
                  className="w-full pl-10 pr-4 py-3.5 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 bg-white focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition placeholder:text-gray-300"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">
                Password
              </label>
              <div className="relative">
                <LockOutlined
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  style={{ fontSize: 18 }}
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrMsg("");
                  }}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3.5 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 bg-white focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition placeholder:text-gray-300"
                />
              </div>
            </div>

            {/* Remember me */}
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <div
                onClick={() => setRememberMe((v) => !v)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition flex-shrink-0 ${
                  rememberMe
                    ? "bg-[#000C66] border-[#000C66]"
                    : "bg-white border-gray-300"
                }`}
              >
                {rememberMe && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 10 10"
                  >
                    <path
                      d="M1.5 5l2.5 2.5 4.5-4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span className="text-sm font-abel text-gray-500">
                Remember me on this device
              </span>
            </label>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#000C66] text-white py-3.5 rounded-xl font-abel text-base font-semibold hover:bg-blue-900 active:scale-[0.99] transition-all mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign In <Login fontSize="small" />
                </>
              )}
            </button>
          </div>

          <p className="text-center text-gray-300 text-xs font-abel mt-10">
            Salvation Ministry Administration © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
